import * as z from "zod";

import { CustomFieldSchema } from "@/domain/custom-field/models/custom-field-model";
import { FocusAreaInstanceSchema } from "@/domain/focus-area/models/focus-area-model";
import { LabelInstanceSchema } from "@/domain/label/models/label-instance-model";
import { MilestoneSchema } from "@/domain/milestone/models/milestone-model";
import { PhaseInstanceSchema } from "@/domain/phase/models/phase-model";
import { PriorityInstanceSchema } from "@/domain/priority/models/priority-model";
import { StatusInstanceSchema } from "@/domain/status/models/status-model";
import { TaskSchema } from "@/domain/task/models/task-model";
import { TeamInstanceSchema } from "@/domain/team/models/team-instance-model";
import { AssigneeSchema } from "@/domain/user/models/assignee-model";

export const ProjectInstanceSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  ownerId: z.string(),
  ownerType: z.string(),
  workspaceId: z.string(),
  project: z
    .object({
      id: z.string(),
      phaseInstance: PhaseInstanceSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
});

export type ProjectInstance = z.infer<typeof ProjectInstanceSchema>;

export const ProjectSchema = z.object({
  __typename: z.literal("Project").optional(),
  id: z.string(),
  workspaceId: z.string(),
  title: z.string(),
  properties: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  yearlyCosts: z.number().nullable().optional(),
  progressPercent: z.number().int().min(0).max(100).nullable().optional(),
  reductionImpact: z.number().nullable().optional(),
  startDate: z
    .union([z.date(), z.string(), z.null()])
    .transform((val) => {
      if (val === null || val === undefined) return null;
      if (val instanceof Date) return val;
      if (typeof val === "string") return new Date(val);
      return null;
    })
    .nullable()
    .optional(),
  endDate: z
    .union([z.date(), z.string(), z.null()])
    .transform((val) => {
      if (val === null || val === undefined) return null;
      if (val instanceof Date) return val;
      if (typeof val === "string") return new Date(val);
      return null;
    })
    .nullable()
    .optional(),
  createdAt: z.union([z.date(), z.string()]).transform((val) => {
    if (val instanceof Date) return val;
    return new Date(val);
  }),
  updatedAt: z.union([z.date(), z.string()]).transform((val) => {
    if (val instanceof Date) return val;
    return new Date(val);
  }),
  teamInstanceList: z.array(TeamInstanceSchema).nullable().optional(),
  focusAreaInstance: FocusAreaInstanceSchema.nullable().optional(),
  labelInstanceList: z.array(LabelInstanceSchema).nullable().optional(),
  projectInstanceList: z.array(ProjectInstanceSchema).nullable().optional(),
  assigneeList: z.array(AssigneeSchema).nullable().optional(),
  phaseInstance: PhaseInstanceSchema.nullable().optional(),
  priorityInstance: PriorityInstanceSchema.nullable().optional(),
  statusInstance: StatusInstanceSchema.nullable().optional(),
  milestoneList: z.array(MilestoneSchema).nullable().optional(),
  taskList: z.array(TaskSchema).nullable().optional(),
  customFieldList: z.array(CustomFieldSchema).nullable().optional(),
});

export type Project = z.infer<typeof ProjectSchema>;
